import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getOrderShipments,
  updateShipmentTracking,
  calculateShipmentShipping,
  updateShipmentFinancials,
  uploadShipmentDocuments,
  listShipmentDocuments,
  deleteShipmentDocument,
  downloadShipmentDocument,
  sendPoToVendor,
  resendPreOrderToVendor,
  confirmShipmentShipping,
  updateShipmentShipping,
  updateShipmentAdditionalCost,
  generateDHLShipment,
} from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const num = (v) => Number(v || 0);
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

// Vendor detection function
const normalizeVendorName = (vendor) => {
  const vendorLower = (vendor || '').toLowerCase().trim();
  if (vendorLower.includes('lenovo-top-choice') || vendorLower === 'lenovo top choice') {
    return 'LENOVO_TOP_CHOICE';
  } else if (vendorLower === 'lcr') {
    return 'LCR';
  } else if (vendorLower.includes('mfp')) {
    return 'MFP';
  } else if (vendorLower.includes('sourcing')) {
    return 'SOURCING_IT';
  }
  return 'UNKNOWN';
};

// Get vendor-specific cost fields for 3-column table
const getVendorCostFields = (shipment, orderPriority) => {
  const vendorType = normalizeVendorName(shipment.vendor);
  const fields = [];

  const shippingBaseCost = shipment.shipping_base_cost || 0;
  const shippingAdditionalCost = shipment.shipping_additional_cost || 0;
  const additionalCost = shipment.additional_cost || 0;
  const shippingConfirmed = shipment.shipping_confirmed || false;
  const hasToBeCalculated = shipment.has_to_be_calculated || false;
  const isExpress = orderPriority === "URGENT";
  // Backend now stores express_fee per shipment (no margin). Use it when set;
  // fall back to old hardcoded values only when the backend value is 0 (old
  // shipments created before the express_fee column existed).
  const backendExpressFee = num(shipment.express_fee);

  if (vendorType === 'LENOVO_TOP_CHOICE') {
    // For Lenovo Top Choice:
    // - shipping_base_cost is the base DHL cost WITHOUT the express fee
    // - shipment.express_fee carries the $215 surcharge (or 0 when not URGENT)
    const dhlBaseCost = shippingBaseCost;

    // DHL Shipping Cost (base + adjustments)
    const totalDhlCost = dhlBaseCost + shippingAdditionalCost;
    fields.push({
      fieldName: "DHL Shipping Cost",
      vendorPrice: totalDhlCost > 0 ? totalDhlCost : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      sellingPrice: totalDhlCost > 0 ? (totalDhlCost / 0.9) : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      isEditable: false,
      fieldType: 'dhl_shipping'
    });

    // Express Cost (Handling Fee) — backend value first, hardcoded fallback for old data
    const expressCost = backendExpressFee > 0 ? backendExpressFee : (isExpress ? 215 : 0);
    fields.push({
      fieldName: "Express Cost (Handling Fee)",
      vendorPrice: expressCost,
      sellingPrice: expressCost,
      isEditable: false,
      fieldType: 'express_cost'
    });

    // Additional Cost
    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  } else if (vendorType === 'LCR') {
    // For LCR:
    // - shipping_base_cost is the base DHL cost WITHOUT the handling fee
    // - shipment.express_fee carries the $215 LCR auto-handling fee
    const dhlBaseCost = shippingBaseCost;

    // DHL Shipping Cost (base + adjustments)
    const totalDhlCost = dhlBaseCost + shippingAdditionalCost;
    fields.push({
      fieldName: "DHL Shipping Cost",
      vendorPrice: totalDhlCost > 0 ? totalDhlCost : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      sellingPrice: totalDhlCost > 0 ? (totalDhlCost / 0.9) : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      isEditable: false,
      fieldType: 'dhl_shipping'
    });

    // Handling Fee — backend value first; LCR is always express so fall back to 215
    const handlingFee = backendExpressFee > 0 ? backendExpressFee : 215;
    fields.push({
      fieldName: "Handling Fee",
      vendorPrice: handlingFee,
      sellingPrice: handlingFee,
      isEditable: false,
      fieldType: 'handling_fee'
    });

    // Additional Cost
    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  } else if (vendorType === 'MFP') {
    // Shipping Cost (base + adjustments)
    const totalShippingCost = shippingBaseCost + shippingAdditionalCost;
    // Before validation the stored base can be 0 (e.g. on-demand items), but the
    // MFP freight-matrix value is still known (backend calculated_shipping =
    // matrix base × 1.10). Show it as an estimate rather than "To be calculated".
    // Display-only; unmapped categories send calculated_shipping = 0 → stay TBC.
    const mfpMatrixBase = num(shipment.calculated_shipping) > 0 ? (num(shipment.calculated_shipping) / 1.10) : 0;
    let mfpVendorPrice, mfpSellingPrice;
    if (totalShippingCost > 0) {
      mfpVendorPrice = totalShippingCost;
      mfpSellingPrice = totalShippingCost / 0.9;
    } else if (mfpMatrixBase > 0) {
      mfpVendorPrice = mfpMatrixBase;
      mfpSellingPrice = mfpMatrixBase / 0.9;
    } else {
      mfpVendorPrice = (hasToBeCalculated && !shippingConfirmed) ? "To be calculated" : 0;
      mfpSellingPrice = (hasToBeCalculated && !shippingConfirmed) ? "To be calculated" : 0;
    }
    fields.push({
      fieldName: (totalShippingCost === 0 && mfpMatrixBase > 0) ? "Shipping Cost (estimated)" : "Shipping Cost",
      vendorPrice: mfpVendorPrice,
      sellingPrice: mfpSellingPrice,
      isEditable: false,
      fieldType: 'shipping_cost'
    });
    
    // Additional Cost
    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  } else if (vendorType === 'SOURCING_IT') {
    // Shipping Cost (base + adjustments)
    const totalShippingCost = shippingBaseCost + shippingAdditionalCost;
    fields.push({
      fieldName: "Shipping Cost",
      vendorPrice: (hasToBeCalculated && !shippingConfirmed) ? "To be calculated" : (totalShippingCost > 0 ? totalShippingCost : 0),
      sellingPrice: (hasToBeCalculated && !shippingConfirmed) ? "To be calculated" : (totalShippingCost > 0 ? (totalShippingCost / 0.9) : 0),
      isEditable: false,
      fieldType: 'shipping_cost'
    });
    
    // Additional Cost
    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  } else if (backendExpressFee > 0) {
    // Generic DHL vendor (e.g. dev_test_dhl, future vendors with
    // Vendor.shipping_method='dhl'): mirror the Lenovo Top Choice layout
    // when the backend reports a non-zero express_fee on the shipment.
    const totalDhlCost = shippingBaseCost + shippingAdditionalCost;
    fields.push({
      fieldName: "DHL Shipping Cost",
      vendorPrice: totalDhlCost > 0 ? totalDhlCost : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      sellingPrice: totalDhlCost > 0 ? (totalDhlCost / 0.9) : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      isEditable: false,
      fieldType: 'dhl_shipping'
    });
    fields.push({
      fieldName: "Express Cost (Handling Fee)",
      vendorPrice: backendExpressFee,
      sellingPrice: backendExpressFee,
      isEditable: false,
      fieldType: 'express_cost'
    });
    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  } else {
    // Fallback for unknown vendors with no express fee - show generic fields
    fields.push({
      fieldName: "Shipping Cost",
      vendorPrice: shippingBaseCost > 0 ? shippingBaseCost : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      sellingPrice: shippingBaseCost > 0 ? (shippingBaseCost / 0.9) : (hasToBeCalculated && !shippingConfirmed ? "To be calculated" : 0),
      isEditable: false,
      fieldType: 'shipping_cost'
    });

    fields.push({
      fieldName: "Additional Cost",
      vendorPrice: additionalCost,
      sellingPrice: additionalCost > 0 ? (additionalCost / 0.9) : 0,
      isEditable: true,
      fieldType: 'additional_cost'
    });
  }
  
  return fields;
};

export default function ShipmentDetails({ orderId, orderItems, isAdmin = true, orderStatus = null, orderPriority = null, onShipmentCountChange, onShipmentsUpdated, readOnly = false }) {
  const { showToast } = useToast();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [trackingLinks, setTrackingLinks] = useState({});
  const [updatingTracking, setUpdatingTracking] = useState({});
  const [uploadingDocs, setUploadingDocs] = useState({});
  const [documents, setDocuments] = useState({}); // { shipmentId: [docs] }
  const [loadingDocs, setLoadingDocs] = useState({});
  const [sendingPo, setSendingPo] = useState({});
  const [poSent, setPoSent] = useState({});
  const [resendingPreOrder, setResendingPreOrder] = useState({});
  const [editingFinancials, setEditingFinancials] = useState({}); // { shipmentId: { shipping: value, tax: value } }
  const [updatingFinancials, setUpdatingFinancials] = useState({}); // { shipmentId: true/false }
  const [confirmingShipping, setConfirmingShipping] = useState({}); // { shipmentId: true/false }
  const [updatingShipping, setUpdatingShipping] = useState({}); // { shipmentId: true/false }
  const [updatingAdditionalCost, setUpdatingAdditionalCost] = useState({}); // { shipmentId: true/false }
  const [shippingAdjustment, setShippingAdjustment] = useState({}); // { shipmentId: value }
  const [additionalCostValue, setAdditionalCostValue] = useState({}); // { shipmentId: value }
  const [showConfirmShippingInput, setShowConfirmShippingInput] = useState({}); // { shipmentId: true/false }
  const [confirmShippingAmount, setConfirmShippingAmount] = useState({}); // { shipmentId: value }
  const [generatingDhl, setGeneratingDhl] = useState({}); // { shipmentId: true/false }

  // Generate a DHL shipment for a single shipment, then download the label PDF locally.
  const handleGenerateDhlShipment = async (shipment) => {
    const shipmentId = shipment.id;
    if (!orderId || generatingDhl[shipmentId]) return;

    setGeneratingDhl(prev => ({ ...prev, [shipmentId]: true }));
    try {
      const res = await generateDHLShipment(orderId, shipmentId);
      const trackingNumber = res?.tracking_number;

      // Decode and download the label PDF locally (if returned)
      if (res?.label_base64) {
        try {
          const byteChars = atob(res.label_base64);
          const byteNumbers = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = res.filename || `DHL_Label_${trackingNumber || shipment.shipment_number}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } catch (decodeErr) {
          console.error('Failed to decode DHL label:', decodeErr);
          showToast('Shipment created but label could not be downloaded', 'warning');
        }
      }

      showToast(
        `DHL shipment created${trackingNumber ? ` (tracking: ${trackingNumber})` : ''}`,
        'success'
      );
      // Refresh shipments to reflect the new tracking link
      fetchShipments();
    } catch (error) {
      console.error('Failed to generate DHL shipment:', error);
      showToast(`Failed to generate DHL shipment: ${error?.message || error}`, 'error');
    } finally {
      setGeneratingDhl(prev => {
        const updated = { ...prev };
        delete updated[shipmentId];
        return updated;
      });
    }
  };

  useEffect(() => {
    if (orderId) {
      fetchShipments();
    }
  }, [orderId]);

  // Map shipment items to order items for display
  const getShipmentItems = (shipment) => {
    if (!shipment.items || !orderItems) return [];
    
    if (isAdmin) {
      // Admin: Map by order_item_id (direct match with order_items.id)
      const shipmentItemIds = shipment.items.map(si => si.order_item_id);
      return orderItems.filter(item => 
        shipmentItemIds.includes(item.id) && 
        (item.order_item_status !== 'exclude')
      );
    } else {
      // User: Map by product_id (since user_order_items have different IDs but same product_id)
      const shipmentProductIds = shipment.items
        .map(si => si.product_id)
        .filter(id => id != null);
      return orderItems.filter(item => 
        shipmentProductIds.includes(item.product_id) && 
        (item.order_item_status !== 'exclude')
      );
    }
  };

  // Filter out shipments with no available items (all items excluded or unavailable)
  // Use useMemo to calculate this before early returns
  const availableShipments = useMemo(() => {
    if (!shipments || shipments.length === 0) return [];
    return shipments.filter(shipment => {
      const shipmentItems = getShipmentItems(shipment);
      // Check if shipment has at least one available item
      return shipmentItems.length > 0 && shipmentItems.some(item => {
        const availability = item.availability || 'available';
        const orderItemStatus = item.order_item_status || 'include';
        return availability !== 'unavailable' && orderItemStatus !== 'exclude';
      });
    });
  }, [shipments, orderItems, isAdmin]);

  // Update shipment count based on available shipments (not all shipments)
  const shipmentCount = availableShipments.length;
  
  // Notify parent of updated shipment count when it changes
  useEffect(() => {
    if (onShipmentCountChange) {
      onShipmentCountChange(shipmentCount);
    }
  }, [shipmentCount, onShipmentCountChange]);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const data = await getOrderShipments(orderId);
      setShipments(data || []);
      
      // Notify parent of shipment count
      if (onShipmentCountChange) {
        onShipmentCountChange(data?.length || 0);
      }
      
      // Initialize expanded state (expand all by default)
      const initialExpanded = {};
      const initialTracking = {};
      data?.forEach(shipment => {
        initialExpanded[shipment.id] = true;
        initialTracking[shipment.id] = shipment.tracking_link || '';
      });
      setExpanded(initialExpanded);
      setTrackingLinks(initialTracking);
      
      // Fetch documents for each shipment
      if (data && data.length > 0) {
        data.forEach(shipment => {
          fetchShipmentDocuments(shipment.id);
        });
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
      showToast('Failed to load shipments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchShipmentDocuments = async (shipmentId) => {
    try {
      setLoadingDocs(prev => ({ ...prev, [shipmentId]: true }));
      const docs = await listShipmentDocuments(orderId, shipmentId);
      setDocuments(prev => ({ ...prev, [shipmentId]: docs || [] }));
    } catch (error) {
      console.error(`Error fetching documents for shipment ${shipmentId}:`, error);
      setDocuments(prev => ({ ...prev, [shipmentId]: [] }));
    } finally {
      setLoadingDocs(prev => ({ ...prev, [shipmentId]: false }));
    }
  };

  const handleAccordionChange = (shipmentId) => (event, isExpanded) => {
    setExpanded(prev => ({ ...prev, [shipmentId]: isExpanded }));
  };

  const handleTrackingChange = (shipmentId, value) => {
    setTrackingLinks(prev => ({ ...prev, [shipmentId]: value }));
  };

  const handleUpdateTracking = async (shipmentId) => {
    try {
      setUpdatingTracking(prev => ({ ...prev, [shipmentId]: true }));
      const trackingLink = trackingLinks[shipmentId] || '';
      await updateShipmentTracking(orderId, shipmentId, trackingLink);
      showToast('Tracking link updated successfully', 'success');
      // Refresh shipments to get updated data
      await fetchShipments();
    } catch (error) {
      console.error('Error updating tracking:', error);
      showToast('Failed to update tracking link', 'error');
    } finally {
      setUpdatingTracking(prev => ({ ...prev, [shipmentId]: false }));
    }
  };

  const handleFileUpload = async (shipmentId, event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file sizes (10MB max)
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      showToast(`Some files exceed 10MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`, 'error');
      return;
    }

    try {
      setUploadingDocs(prev => ({ ...prev, [shipmentId]: true }));
      await uploadShipmentDocuments(orderId, shipmentId, files);
      showToast('Documents uploaded successfully', 'success');
      // Refresh documents
      await fetchShipmentDocuments(shipmentId);
    } catch (error) {
      console.error('Error uploading documents:', error);
      showToast('Failed to upload documents', 'error');
    } finally {
      setUploadingDocs(prev => ({ ...prev, [shipmentId]: false }));
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (shipmentId, documentId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      await deleteShipmentDocument(orderId, shipmentId, documentId);
      showToast('Document deleted successfully', 'success');
      // Refresh documents
      await fetchShipmentDocuments(shipmentId);
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast('Failed to delete document', 'error');
    }
  };

  const handleDownloadDocument = async (shipmentId, documentId, fileName) => {
    try {
      await downloadShipmentDocument(orderId, shipmentId, documentId, fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
      showToast('Failed to download document', 'error');
    }
  };

  // Calculate shipment totals
  const calculateShipmentTotals = (shipment) => {
    const items = getShipmentItems(shipment);
    const subtotal = round2(
      items.reduce((sum, item) => {
        // Exclude unavailable items from calculations
        if (item.availability === 'unavailable') return sum;
        // Exclude items with order_item_status === 'exclude'
        if (item.order_item_status === 'exclude') return sum;
        
        const unitPrice = num(item.unit_price ?? item.product_price ?? 0);
        const qty = num(item.quantity ?? 1);
        // Exclude ON_DEMAND items with no price
        const isOnDemand = (item.pricing_mode === 'ON_DEMAND' && (unitPrice === 0 || !unitPrice)) ||
                          (item.pricing_mode !== 'FIXED' && (unitPrice === 0 || !unitPrice));
        if (isOnDemand) return sum;
        return sum + (unitPrice * qty);
      }, 0)
    );
    
    const shipping = round2(num(shipment.shipping_cost || 0));
    const tax = round2(num(shipment.tax_amount || 0));
    const billable = round2(subtotal + shipping + tax);
    
    return { subtotal, shipping, tax, billable };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Calculate shipping for a shipment using freight rules/DHL API
  const handleCalculateShipping = async (shipmentId) => {
    setUpdatingFinancials(prev => ({ ...prev, [shipmentId]: true }));
    try {
      await calculateShipmentShipping(orderId, shipmentId);
      showToast('Shipping calculated successfully!', 'success');
      // Refresh shipments to get updated values
      await fetchShipments();
    } catch (error) {
      console.error('Failed to calculate shipping:', error);
      showToast('Failed to calculate shipping.', 'error');
    } finally {
      setUpdatingFinancials(prev => ({ ...prev, [shipmentId]: false }));
    }
  };

  // Handle shipping update for a shipment (manual override)
  const handleUpdateFinancials = async (shipmentId, shippingValue) => {
    setUpdatingFinancials(prev => ({ ...prev, [shipmentId]: true }));
    try {
      await updateShipmentFinancials(orderId, shipmentId, shippingValue);
      showToast('Shipment shipping updated successfully!', 'success');
      // Refresh shipments to get updated values
      await fetchShipments();
      // Clear editing state
      setEditingFinancials(prev => {
        const newState = { ...prev };
        delete newState[shipmentId];
        return newState;
      });
    } catch (error) {
      console.error('Failed to update shipment shipping:', error);
      showToast('Failed to update shipment shipping.', 'error');
    } finally {
      setUpdatingFinancials(prev => ({ ...prev, [shipmentId]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shipments || shipments.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No shipments found. Shipments will be created automatically when order is confirmed.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {availableShipments.map((shipment, index) => {
        const shipmentItems = getShipmentItems(shipment);
        const totals = calculateShipmentTotals(shipment);
        const shipmentDocs = documents[shipment.id] || [];
        const isExpanded = expanded[shipment.id] || false;
        const trackingLink = trackingLinks[shipment.id] || '';
        const isUpdating = updatingTracking[shipment.id] || false;
        const isUploading = uploadingDocs[shipment.id] || false;
        const isLoadingDocs = loadingDocs[shipment.id] || false;
        const isUpdatingFinancials = updatingFinancials[shipment.id] || false;
        
        // Get editing values for this shipment (or use current values)
        const editingFin = editingFinancials[shipment.id] || {};
        const currentShipping = editingFin.shipping !== undefined ? editingFin.shipping : totals.shipping;
        // Tax is auto-calculated, not editable
        const currentTax = totals.tax;
        
        // Check if any item in shipment has no shipping rule (user portal only)
        const hasNoShippingRule = !isAdmin && shipment.items?.some(item => item.has_shipping_rule === false);
        
        // Get shipping information for this shipment
        const shipmentShipping = shipment.shipping_cost || 0;
        const shipmentBaseCost = shipment.shipping_base_cost || 0; // Base cost WITHOUT margin
        const shipmentAdjShipping = shipment.shipping_additional_cost || 0; // Adjustments from increase/decrease
        const shipmentAdditionalCost = shipment.additional_cost || 0; // Separate additional cost field
        const hasToBeCalculated = shipment.has_to_be_calculated || false;
        const shippingConfirmed = shipment.shipping_confirmed || false;
        const calculatedShipping = shipment.calculated_shipping || shipmentShipping;
        const isConfirming = confirmingShipping[shipment.id] || false;
        const isUpdatingShipmentShipping = updatingShipping[shipment.id] || false;
        const isUpdatingShipmentAdditionalCost = updatingAdditionalCost[shipment.id] || false;
        
        // Admin Reference: Show ONLY shipping_base_cost + shipping_additional_cost (raw value, NO margin)
        // This is the actual value from shipping rules, without any 10% margin
        // DO NOT use calculatedShipping as fallback (it has 10% margin applied)
        // shipping_additional_cost is updated when user uses increase/decrease shipping
        // Note: This is NOT the same as shipment.additional_cost (which is a separate field)
        const adminReferenceShipping = (shipmentBaseCost || 0) + (shipmentAdjShipping || 0);
        
        // For Admin Reference, only show value if shipping_base_cost exists
        // If shipping_base_cost is 0, show "To be calculated" (don't use calculatedShipping with margin)
        // MFP freight-matrix shipping is known even before validation (the backend
        // sends calculated_shipping = matrix base × 1.10). For MFP, when no base is
        // stored yet, show that matrix base as an estimate instead of
        // "To be calculated". Display-only — the stored value is unchanged.
        const isMfpVendor = normalizeVendorName(shipment.vendor) === 'MFP';
        const mfpMatrixBase = num(shipment.calculated_shipping) > 0 ? (num(shipment.calculated_shipping) / 1.10) : 0;
        let adminShippingDisplay;
        if (shipmentBaseCost > 0 || shipmentAdjShipping !== 0) {
          // Show actual Admin Reference value (raw, no margin)
          adminShippingDisplay = `$${adminReferenceShipping.toFixed(2)}`;
        } else if (isMfpVendor && mfpMatrixBase > 0) {
          // MFP matrix value known pre-validation (may be partial in a mixed cart) —
          // show the available value as an estimate instead of "To be calculated".
          adminShippingDisplay = `$${mfpMatrixBase.toFixed(2)} (estimated)`;
        } else {
          // No stored base and no matrix value available - show "To be calculated"
          adminShippingDisplay = 'To be calculated';
        }
        
        // For backward compatibility, keep shippingDisplay for other uses
        const displayShipping = shipmentShipping > 0 ? shipmentShipping : calculatedShipping;
        let shippingDisplay = `$${displayShipping.toFixed(2)}`;
        if (hasToBeCalculated && !shippingConfirmed) {
          shippingDisplay = 'To be calculated';
        }

        return (
          <Accordion
            key={shipment.id}
            expanded={isExpanded}
            onChange={handleAccordionChange(shipment.id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {shipment.shipment_number}
                      {shipmentCount > 1 && (
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', ml: 1, fontWeight: 'normal' }}>
                          (Shipment {index + 1} of {shipmentCount})
                        </Typography>
                      )}
                    </Typography>
                    {isAdmin && (
                      <Typography variant="body1" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                        {shipment.vendor}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* Re-send Pre-Order to Vendor Button - Only for admin, only when order status is VENDOR VALIDATION REQUESTED */}
                {isAdmin && !readOnly && orderStatus === 'VENDOR VALIDATION REQUESTED' && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation(); // Prevent accordion from expanding/collapsing
                      const shipmentId = shipment.id;
                      const vendorName = shipment.vendor;
                      
                      if (!orderId || !vendorName || resendingPreOrder[shipmentId]) {
                        console.log('Skipping resend - orderId:', orderId, 'vendor:', vendorName, 'already sending:', resendingPreOrder[shipmentId]);
                        return;
                      }
                      
                      setResendingPreOrder(prev => ({ ...prev, [shipmentId]: true }));
                      try {
                        console.log('Sending pre-order email for order:', orderId, 'vendor:', vendorName);
                        // Pass the vendor from this shipment
                        await resendPreOrderToVendor(orderId, vendorName);
                        showToast(`Pre-order email sent successfully to ${vendorName}`, 'success');
                      } catch (error) {
                        console.error('Failed to resend pre-order:', error);
                        showToast(`Failed to resend pre-order: ${error?.message || error}`, 'error');
                      } finally {
                        setResendingPreOrder(prev => {
                          const updated = { ...prev };
                          delete updated[shipmentId];
                          return updated;
                        });
                      }
                    }}
                    disabled={resendingPreOrder[shipment.id]}
                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', ml: 'auto' }}
                  >
                    {resendingPreOrder[shipment.id] ? 'Sending...' : 'Re-send Pre-Order to Vendor'}
                  </Button>
                )}
                {/* Send PO to Vendor Button - Only for admin, only when order status is VENDOR ORDER PROCESSED */}
                {isAdmin && !readOnly && orderStatus === 'VENDOR ORDER PROCESSED' && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation(); // Prevent accordion from expanding/collapsing
                      const shipmentId = shipment.id;
                      const vendorName = shipment.vendor;
                      if (!orderId || !vendorName || sendingPo[shipmentId]) return;
                      
                      setSendingPo(prev => ({ ...prev, [shipmentId]: true }));
                      try {
                        await sendPoToVendor(orderId, vendorName);
                        setPoSent(prev => ({ ...prev, [shipmentId]: true }));
                        showToast(`PO email sent successfully to ${vendorName}`, 'success');
                      } catch (error) {
                        console.error('Failed to send PO:', error);
                        showToast(`Failed to send PO: ${error?.message || error}`, 'error');
                      } finally {
                        setSendingPo(prev => {
                          const newState = { ...prev };
                          delete newState[shipmentId];
                          return newState;
                        });
                      }
                    }}
                    disabled={sendingPo[shipment.id]}
                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', ml: 'auto' }}
                  >
                    {sendingPo[shipment.id] ? 'Sending...' : (poSent[shipment.id] ? 'Re-send PO to Vendor' : 'Send PO to Vendor')}
                  </Button>
                )}
                {/* Generate DHL Shipment Button - admin only, DHL vendors only, only when order is ASSEMBLING ORDER */}
                {isAdmin && !readOnly && orderStatus === 'ASSEMBLING ORDER' && shipment.is_dhl && (
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion from expanding/collapsing
                      handleGenerateDhlShipment(shipment);
                    }}
                    disabled={generatingDhl[shipment.id]}
                    startIcon={generatingDhl[shipment.id] ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', ml: 'auto' }}
                  >
                    {generatingDhl[shipment.id] ? 'Generating...' : 'Generate DHL Shipment'}
                  </Button>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mt: 2 }}>
                {/* Products Table */}
                <Table size="small" sx={{ mb: 3 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>IMG</TableCell>
                      <TableCell>MFR</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Internal #</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipmentItems.map((item) => {
                      const p = item.product || {};
                      const unitPrice = num(item.unit_price ?? item.product_price ?? 0);
                      const qty = num(item.quantity ?? 1);
                      const total = unitPrice * qty;
                      const isOnDemand = (item.pricing_mode === 'ON_DEMAND' && (unitPrice === 0 || !unitPrice)) ||
                                        (item.pricing_mode !== 'FIXED' && (unitPrice === 0 || !unitPrice));
                      const isUnavailable = item.availability === 'unavailable';
                      
                      // Styling for unavailable items (strikethrough)
                      const cellStyle = isUnavailable ? {
                        textDecoration: 'line-through',
                        opacity: 0.6,
                        color: '#999'
                      } : {};

                      return (
                        <TableRow key={item.id}>
                          <TableCell sx={cellStyle}>
                            <img
                              src={item.snapshot_product_image || p.image_url || p.image_urls?.[0] || "https://via.placeholder.com/50"}
                              alt=""
                              style={{ width: 50, height: 50, objectFit: 'cover', opacity: isUnavailable ? 0.6 : 1 }}
                            />
                          </TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_brand || p.brand || "—"}</TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_description || item.product_name || p.description || "—"}</TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_location || p.location || "—"}</TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_category || p.category || "—"}</TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_condition || p.condition || "New"}</TableCell>
                          <TableCell sx={cellStyle}>{qty}</TableCell>
                          <TableCell sx={cellStyle}>{item.snapshot_product_sku || item.product_sku || p.sku || "—"}</TableCell>
                          <TableCell sx={cellStyle}>
                            {isOnDemand ? (
                              <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                Pending
                              </Typography>
                            ) : (
                              `$${unitPrice.toFixed(2)}`
                            )}
                          </TableCell>
                          <TableCell sx={cellStyle}>
                            {isOnDemand ? (
                              <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                Pending
                              </Typography>
                            ) : (
                              `$${total.toFixed(2)}`
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Tracking Link (Admin only) */}
                {isAdmin && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Tracking Link:
                    </Typography>
                    {readOnly ? (
                      <Typography variant="body2" color="text.secondary">
                        {trackingLink || 'Not set'}
                      </Typography>
                    ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={trackingLink}
                        onChange={(e) => handleTrackingChange(shipment.id, e.target.value)}
                        placeholder="Enter tracking link"
                        disabled={isUpdating}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUpdateTracking(shipment.id)}
                        disabled={isUpdating}
                      >
                        {isUpdating ? 'Updating...' : 'Update'}
                      </Button>
                    </Box>
                    )}
                  </Box>
                )}

                {/* Track Here (User only) */}
                {!isAdmin && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Track Here:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {shipment.tracking_link ? (
                        <a href={shipment.tracking_link} target="_blank" rel="noopener noreferrer">
                          {shipment.tracking_link}
                        </a>
                      ) : (
                        'To be Updated'
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Shipping Adjustment and Additional Cost (Admin only) */}
                {isAdmin && !readOnly && (() => {
                  const vendorFields = getVendorCostFields(shipment, orderPriority);
                  const vendorType = normalizeVendorName(shipment.vendor);
                  // Manual shipping entry is driven by the vendor's configured
                  // shipping_method (Vendor.shipping_method === 'manual'), resolved by the
                  // backend onto shipment.vendor_shipping_method. This covers every manual
                  // vendor (Advent, Cyberpulse, FuturDist, Sourcing IT) instead of matching
                  // a hardcoded vendor name. Fall back to the legacy name match only when
                  // the API didn't supply the field (older shipments / vendor not found).
                  const vendorMethod = (shipment.vendor_shipping_method || '').toLowerCase();
                  const isManualVendor = vendorMethod
                    ? vendorMethod === 'manual'
                    : vendorType === 'SOURCING_IT';
                  const showConfirmShipping = isManualVendor && hasToBeCalculated && !shippingConfirmed;
                  
                  return (
                    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Shipment Cost Management
                      </Typography>
                      
                      {/* Vendor-specific 3-column cost table */}
                      <Table size="small" sx={{ mb: 2, mt: 1 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Vendor Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Selling Price to Customer (Includes 10% Margin)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vendorFields.map((field, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{field.fieldName}</TableCell>
                              <TableCell align="right">
                                {field.vendorPrice === "To be calculated" ? (
                                  <Typography sx={{ color: '#ff9800', fontWeight: 600 }}>
                                    To be calculated
                                  </Typography>
                                ) : typeof field.vendorPrice === 'number' ? (
                                  `$${field.vendorPrice.toFixed(2)}`
                                ) : (
                                  field.vendorPrice
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {field.sellingPrice === "To be calculated" ? (
                                  <Typography sx={{ color: '#ff9800', fontWeight: 600 }}>
                                    To be calculated
                                  </Typography>
                                ) : typeof field.sellingPrice === 'number' ? (
                                  <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                                    ${field.sellingPrice.toFixed(2)}
                                  </Typography>
                                ) : (
                                  field.sellingPrice
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* Confirm Shipping Input (Sourcing IT only) */}
                      {showConfirmShipping && (
                        <Box sx={{ mb: 2 }}>
                          {!showConfirmShippingInput[shipment.id] ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                  setShowConfirmShippingInput(prev => ({ ...prev, [shipment.id]: true }));
                                  setConfirmShippingAmount(prev => ({ ...prev, [shipment.id]: calculatedShipping > 0 ? calculatedShipping.toFixed(2) : '' }));
                                }}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Confirm Shipping
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                fullWidth
                                required
                                label="Enter shipping amount"
                                value={confirmShippingAmount[shipment.id] || ''}
                                onChange={(e) => setConfirmShippingAmount(prev => ({ ...prev, [shipment.id]: e.target.value }))}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ flex: 1 }}
                                autoFocus
                                error={!confirmShippingAmount[shipment.id] || parseFloat(confirmShippingAmount[shipment.id] || 0) < 0}
                                helperText={!confirmShippingAmount[shipment.id] ? 'Shipping amount is required' : (parseFloat(confirmShippingAmount[shipment.id] || 0) < 0 ? 'Amount cannot be negative' : '')}
                              />
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={async () => {
                                  if (!orderId || isConfirming) return;
                                  const amountStr = confirmShippingAmount[shipment.id];
                                  if (!amountStr || amountStr.trim() === '') {
                                    showToast('Please enter a shipping amount', 'warning');
                                    return;
                                  }
                                  const amount = parseFloat(amountStr);
                                  if (isNaN(amount) || amount < 0) {
                                    showToast('Please enter a valid shipping amount', 'warning');
                                    return;
                                  }
                                  setConfirmingShipping(prev => ({ ...prev, [shipment.id]: true }));
                                  try {
                                    await confirmShipmentShipping(orderId, shipment.id, amount);
                                    setShowConfirmShippingInput(prev => ({ ...prev, [shipment.id]: false }));
                                    setConfirmShippingAmount(prev => ({ ...prev, [shipment.id]: '' }));
                                    await fetchShipments();
                                    if (onShipmentsUpdated) {
                                      onShipmentsUpdated();
                                    }
                                    showToast('Shipping confirmed successfully', 'success');
                                  } catch (error) {
                                    console.error('Failed to confirm shipping:', error);
                                    showToast(`Failed to confirm shipping: ${error?.message || error}`, 'error');
                                  } finally {
                                    setConfirmingShipping(prev => ({ ...prev, [shipment.id]: false }));
                                  }
                                }}
                                disabled={isConfirming}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {isConfirming ? 'Confirming...' : 'SET'}
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setShowConfirmShippingInput(prev => ({ ...prev, [shipment.id]: false }));
                                  setConfirmShippingAmount(prev => ({ ...prev, [shipment.id]: '' }));
                                }}
                                disabled={isConfirming}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Adjust Shipping +/- (All vendors) */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Adjust Shipping +/-:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={shippingAdjustment[shipment.id] || ''}
                            onChange={(e) => setShippingAdjustment(prev => ({ ...prev, [shipment.id]: e.target.value }))}
                            placeholder="Use + to increase, - to decrease"
                            disabled={isUpdatingShipmentShipping || (hasToBeCalculated && !shippingConfirmed)}
                            helperText={hasToBeCalculated && !shippingConfirmed ? "Shipping must be confirmed before adjustment" : "Use + to increase, - to decrease"}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                              if (!orderId || isUpdatingShipmentShipping) return;
                              const adjustmentValue = shippingAdjustment[shipment.id] || '';
                              if (!adjustmentValue || adjustmentValue.trim() === '') {
                                showToast('Please enter an adjustment amount', 'warning');
                                return;
                              }
                              
                              let adjustment = parseFloat(adjustmentValue);
                              if (isNaN(adjustment)) {
                                showToast('Please enter a valid number', 'warning');
                                return;
                              }
                              
                              if (adjustment === 0) {
                                showToast('Adjustment amount cannot be zero', 'warning');
                                return;
                              }
                              
                              setUpdatingShipping(prev => ({ ...prev, [shipment.id]: true }));
                              try {
                                await updateShipmentShipping(orderId, shipment.id, adjustment);
                                setShippingAdjustment(prev => ({ ...prev, [shipment.id]: '' }));
                                await fetchShipments();
                                if (onShipmentsUpdated) {
                                  onShipmentsUpdated();
                                }
                                showToast(`Shipping ${adjustment > 0 ? 'increased' : 'decreased'} by $${Math.abs(adjustment).toFixed(2)}`, 'success');
                              } catch (error) {
                                console.error('Failed to update shipping:', error);
                                showToast(`Failed to update shipping: ${error?.message || error}`, 'error');
                              } finally {
                                setUpdatingShipping(prev => ({ ...prev, [shipment.id]: false }));
                              }
                            }}
                            disabled={isUpdatingShipmentShipping || (hasToBeCalculated && !shippingConfirmed)}
                          >
                            {isUpdatingShipmentShipping ? 'Updating...' : 'UPDATE'}
                          </Button>
                        </Box>
                      </Box>

                      {/* Additional Cost */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Additional Cost (Shipment {index + 1}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={additionalCostValue[shipment.id] !== undefined ? additionalCostValue[shipment.id] : shipmentAdditionalCost}
                            onChange={(e) => setAdditionalCostValue(prev => ({ ...prev, [shipment.id]: e.target.value }))}
                            placeholder="Enter additional cost"
                            disabled={isUpdatingShipmentAdditionalCost}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={async () => {
                              if (!orderId || isUpdatingShipmentAdditionalCost) return;
                              const cost = parseFloat(additionalCostValue[shipment.id] || 0);
                              if (isNaN(cost) || cost < 0) {
                                showToast('Please enter a valid cost amount', 'warning');
                                return;
                              }
                              setUpdatingAdditionalCost(prev => ({ ...prev, [shipment.id]: true }));
                              try {
                                await updateShipmentAdditionalCost(orderId, shipment.id, cost);
                                await fetchShipments();
                                if (onShipmentsUpdated) {
                                  onShipmentsUpdated();
                                }
                                showToast('Additional cost updated successfully', 'success');
                              } catch (error) {
                                console.error('Failed to update additional cost:', error);
                                showToast(`Failed to update additional cost: ${error?.message || error}`, 'error');
                              } finally {
                                setUpdatingAdditionalCost(prev => ({ ...prev, [shipment.id]: false }));
                              }
                            }}
                            disabled={isUpdatingShipmentAdditionalCost}
                          >
                            {isUpdatingShipmentAdditionalCost ? 'Updating...' : 'UPDATE'}
                          </Button>
                        </Box>
                      </Box>

                      {/* Buyer's Shipping Value (Display only - saved on validation) */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Buyer's Shipping Value (Shipping + Additional Cost (Includes 10% Margin)):
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600,
                          p: 1,
                          bgcolor: '#fff3e0',
                          borderRadius: 1,
                          color: 'text.primary'
                        }}>
                          {(() => {
                            if (shipment.buyer_shipping_value && shipment.buyer_shipping_value > 0) {
                              return `$${shipment.buyer_shipping_value.toFixed(2)}`;
                            }
                            
                            if (hasToBeCalculated && !shippingConfirmed) {
                              return 'To be calculated';
                            }
                            
                            // Calculate Buyer's Shipping Value = Sum of "Selling Price to Customer" column
                            // This is the sum of all sellingPrice values (which already have margin applied where needed)
                            let totalSellingPrice = 0;
                            
                            vendorFields.forEach(field => {
                              // Sum all selling prices (which already have margin applied correctly)
                              // Handling fee sellingPrice is $215 (NO margin), DHL and Additional Cost have margin
                              if (typeof field.sellingPrice === 'number' && field.sellingPrice > 0) {
                                totalSellingPrice += field.sellingPrice;
                              } else if (field.sellingPrice === 'To be calculated') {
                                // Skip "To be calculated" fields
                                return;
                              }
                            });
                            
                            return `$${totalSellingPrice.toFixed(2)}`;
                          })()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                          {shipment.buyer_shipping_value && shipment.buyer_shipping_value > 0 
                            ? 'Saved value (will be used in Order Summary)' 
                            : 'Calculated value (will be saved on Validate Order)'}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Upload Documents (Admin only) */}
                {isAdmin && !readOnly && (
                  <Box sx={{ mb: 3 }}>
                    <input
                      accept="*/*"
                      style={{ display: 'none' }}
                      id={`upload-shipment-${shipment.id}`}
                      type="file"
                      multiple
                      onChange={(e) => handleFileUpload(shipment.id, e)}
                    />
                    <label htmlFor={`upload-shipment-${shipment.id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                      >
                        {isUploading ? 'Uploading...' : 'Upload Shipment Documents'}
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Max 10MB per file
                    </Typography>
                  </Box>
                )}

                {/* Documents List */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {shipment.shipment_number} Documents:
                  </Typography>
                  {isLoadingDocs ? (
                    <CircularProgress size={20} />
                  ) : shipmentDocs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No documents uploaded
                    </Typography>
                  ) : (
                    <List dense>
                      {shipmentDocs.map((doc) => (
                        <ListItem key={doc.id} divider>
                          <ListItemText
                            primary={doc.file_name}
                            secondary={`Size: ${formatFileSize(doc.file_size)} | Uploaded: ${new Date(doc.uploaded_at).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Download">
                              <IconButton
                                edge="end"
                                onClick={() => handleDownloadDocument(shipment.id, doc.id, doc.file_name)}
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && !readOnly && (
                              <Tooltip title="Delete">
                                <IconButton
                                  edge="end"
                                  onClick={() => handleDeleteDocument(shipment.id, doc.id, doc.file_name)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

